import React, { useEffect, useState } from 'react';
import config from '../../config';
import {
  Box,
  Typography,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
  TextField,
  Grid,
  CircularProgress,
  Divider,
  Chip,
  Alert,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import { motion } from 'framer-motion';
import { toast } from 'react-toastify';
import { useRoomNames } from './useRoomNames';

export default function ChangeRoom() {
  const { roomLabel } = useRoomNames();
  const [bookings, setBookings] = useState([]);
  const [availableRooms, setAvailableRooms] = useState([]);
  const [loadingBookings, setLoadingBookings] = useState(false);
  const [loadingRooms, setLoadingRooms] = useState(false);
  // Prevents a double-click / double-submit of the room change.
  const [submitting, setSubmitting] = useState(false);

  const [selectedBooking, setSelectedBooking] = useState(null);
  const [step, setStep] = useState(1);

  const [selectedOldRooms, setSelectedOldRooms] = useState([]);
  const [selectedNewRooms, setSelectedNewRooms] = useState([]);
  const [extraCharge, setExtraCharge] = useState('');

  const company = JSON.parse(sessionStorage.getItem('companyDetails') || '{}');
  const Companycurrency = company?.currency ?? '';
  const token = sessionStorage.getItem('token');
  const [remark, setRemark] = useState('');
  // Fetch Active Bookings
  const fetchBookings = async () => {
    if (!company?.id) return;
    setLoadingBookings(true);
    try {
      const res = await fetch(
        `${config.API_BASE_URL}/home/Getallactivebookings/${company.id}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const data = await res.json();
      setBookings(data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingBookings(false);
    }
  };

  // Fetch Available Rooms
  const fetchAvailableRooms = async (bookingId) => {
    if (!company?.id || !bookingId) return;
    setLoadingRooms(true);
    try {
      const res = await fetch(
        `${config.API_BASE_URL}/home/Getallactiverooms/${company.id}/${bookingId}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const data = await res.json();
      console.log(data);
      // Guard against a non-array response (an error object / single object)
      // which made `availableRooms.map` throw and crash the screen.
      setAvailableRooms(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingRooms(false);
    }
  };

  useEffect(() => {
    fetchBookings();
  }, []);

  const handleOpenModal = (booking) => {
    setSelectedBooking(booking);
    setStep(1);
    setSelectedOldRooms([]);
    setSelectedNewRooms([]);
    setExtraCharge('');
    setRemark('');
    fetchAvailableRooms(booking.id);
  };

  const handleClose = () => {
    setSelectedBooking(null);
    setAvailableRooms([]);
  };

  const toggleRoomSelection = (roomNumber, type) => {
    const value = String(roomNumber);
    const setter = type === 'old' ? setSelectedOldRooms : setSelectedNewRooms;

    setter((prev) =>
      prev.includes(value) ? prev.filter((r) => r !== value) : [...prev, value],
    );
  };

  // Remove ONE room from the booking (credits its charge back). Separate from
  // the swap flow above.
  const handleRemoveRoom = async (roomNumber) => {
    if ((selectedBooking?.bookingRoomDetails?.length || 0) <= 1) {
      toast.error('A booking must keep at least one room');
      return;
    }
    if (
      !window.confirm(
        `Remove Room ${roomLabel(roomNumber)} from this booking? Its charge will be credited back.`,
      )
    )
      return;
    try {
      const res = await fetch(`${config.API_BASE_URL}/bookingroom/remove`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          bookingId: selectedBooking.id,
          roomNumber: Number(roomNumber),
          companyId: company?.id,
          remark: remark ?? '',
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || body?.success === false) {
        toast.error(body?.error || 'Failed to remove room');
        return;
      }
      toast.success(body?.message || 'Room removed');
      handleClose();
      fetchBookings();
    } catch (err) {
      console.error('Remove room error:', err);
      toast.error('Failed to remove room');
    }
  };

  const handleSubmit = async () => {
    if (selectedNewRooms.length !== selectedOldRooms.length) return;
    if (submitting) return; // guard against a second click while in-flight
    setSubmitting(true);

    const payload = {
      bookingId: selectedBooking.id,
      oldRooms: selectedOldRooms,
      newRooms: selectedNewRooms,
      extraCharge: Number(extraCharge) || 0,
      remark: remark ?? '',
    };

    try {
      const res = await fetch(`${config.API_BASE_URL}/home/Changeroom`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      // Only close/refresh on real success — backend now returns status 'failed'
      // when the room change didn't apply.
      let ok = res.ok;
      if (ok) {
        try {
          const body = await res.json();
          if (body && body.status && body.status !== 'success') ok = false;
        } catch {}
      }
      if (!ok) {
        toast.error('Room change failed. Please try again.');
        return;
      }

      handleClose();
      fetchBookings();
    } catch (err) {
      console.error(err);
      toast.error('Room change failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const canGoNext = selectedOldRooms.length > 0;

  return (
    <Box
      sx={{
        p: 4,
        background: 'linear-gradient(to right, #f8fafc, #eef2ff)',
        minHeight: '100vh',
      }}>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
        <Typography variant='h4' fontWeight='bold' textAlign='center' mb={4}>
          🔄 Room Change Management
        </Typography>
      </motion.div>

      {/* BOOKINGS CARDS */}
      <Grid container spacing={3}>
        {loadingBookings ? (
          <Box sx={{ width: '100%', textAlign: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          bookings.map((b, i) => (
            <Grid item xs={12} md={6} lg={4} key={b.id || i}>
              <Paper
                elevation={4}
                sx={{
                  p: 3,
                  borderRadius: 3,
                  transition: '0.3s',
                  '&:hover': { transform: 'translateY(-5px)', boxShadow: 8 },
                }}>
                <Typography variant='h6' fontWeight={600}>
                  {b.firstName} {b.lastName}
                </Typography>

                <Typography variant='body2' mt={1}>
                  Check-In: {new Date(b.checkIn).toLocaleDateString('en-GB')}
                </Typography>

                <Typography variant='body2'>
                  Check-Out: {new Date(b.checkOut).toLocaleDateString('en-GB')}
                </Typography>

                <Box mt={2} display='flex' gap={1} flexWrap='wrap'>
                  {b.bookingRoomDetails?.map((r) => (
                    <Chip
                      key={r.roomnumber}
                      label={`Room ${roomLabel(r.roomnumber)}`}
                      color='primary'
                      variant='outlined'
                    />
                  ))}
                </Box>

                <Button
                  fullWidth
                  variant='contained'
                  sx={{ mt: 3, borderRadius: 2 }}
                  onClick={() => handleOpenModal(b)}>
                  Change Room
                </Button>
              </Paper>
            </Grid>
          ))
        )}
      </Grid>

      {/* DIALOG */}
      <Dialog
        open={!!selectedBooking}
        onClose={handleClose}
        maxWidth='md'
        fullWidth>
        <DialogTitle>
          <Stepper activeStep={step - 1} alternativeLabel>
            <Step>
              <StepLabel>Select Old Rooms</StepLabel>
            </Step>
            <Step>
              <StepLabel>Select New Rooms</StepLabel>
            </Step>
            <Step>
              <StepLabel>Review & Confirm</StepLabel>
            </Step>
          </Stepper>
        </DialogTitle>

        <DialogContent dividers>
          {/* STEP 1 */}
          {step === 1 && (
            <>
              <Typography variant='h6' mb={1}>
                Select rooms to change
              </Typography>
              <Typography variant='body2' color='text.secondary' mb={2}>
                Tick a room to SWAP it (next step), or use “Remove” to drop a room
                from this booking — its charge is credited back automatically.
              </Typography>

              {selectedBooking?.bookingRoomDetails?.map((r) => (
                <Box
                  key={r.roomnumber}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    borderBottom: '1px solid #eee',
                    py: 0.5,
                  }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={selectedOldRooms.includes(String(r.roomnumber))}
                        onChange={() => toggleRoomSelection(r.roomnumber, 'old')}
                      />
                    }
                    label={`Room ${roomLabel(r.roomnumber)}`}
                  />
                  {(selectedBooking?.bookingRoomDetails?.length || 0) > 1 && (
                    <Button
                      size='small'
                      color='error'
                      variant='outlined'
                      onClick={() => handleRemoveRoom(r.roomnumber)}>
                      Remove
                    </Button>
                  )}
                </Box>
              ))}
            </>
          )}

          {/* STEP 2 */}
          {step === 2 && (
            <>
              <Typography variant='h6' mb={2}>
                Select New Rooms
              </Typography>

              {loadingRooms ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : availableRooms.length === 0 ? (
                <Alert severity='info'>No rooms available</Alert>
              ) : (
                <Grid container spacing={2}>
                  {availableRooms.map((room) => {
                    const roomNumStr = String(room.roomnumber);
                    const isAvailable =
                      room.status?.toLowerCase() === 'available';

                    const isSelected = selectedNewRooms.includes(roomNumStr);

                    return (
                      <Grid item xs={12} sm={6} md={4} key={roomNumStr}>
                        <Paper
                          onClick={() =>
                            isAvailable &&
                            toggleRoomSelection(room.roomnumber, 'new')
                          }
                          sx={{
                            p: 3,
                            borderRadius: 3,
                            textAlign: 'center',
                            cursor: isAvailable ? 'pointer' : 'not-allowed',
                            transition: '0.3s',
                            border: '2px solid',

                            // 🎨 COLOR LOGIC
                            bgcolor: isAvailable
                              ? isSelected
                                ? '#d1fae5' // darker green if selected
                                : '#ecfdf5' // light green
                              : '#fee2e2', // light red

                            borderColor: isAvailable
                              ? isSelected
                                ? 'success.main'
                                : 'success.light'
                              : 'error.main',

                            opacity: isAvailable ? 1 : 0.7,

                            '&:hover': isAvailable
                              ? {
                                  transform: 'translateY(-4px)',
                                  boxShadow: 6,
                                }
                              : {},
                          }}>
                          <Typography variant='h6' fontWeight={600}>
                            Room {roomLabel(room.roomnumber)}
                          </Typography>

                          <Typography
                            variant='body2'
                            color='text.secondary'
                            mt={1}>
                            {room.roomTypeName}
                          </Typography>

                          <Chip
                            label={room.status}
                            color={isAvailable ? 'success' : 'error'}
                            size='small'
                            sx={{ mt: 2 }}
                          />
                        </Paper>
                      </Grid>
                    );
                  })}
                </Grid>
              )}
            </>
          )}

          {/* STEP 3 */}
          {step === 3 && (
            <>
              <Typography variant='h6' mb={2}>
                Review
              </Typography>

              <Paper sx={{ p: 2, mb: 2 }}>
                Old Rooms:{' '}
                {selectedOldRooms.map((n) => roomLabel(n)).join(', ')}
              </Paper>

              <Paper sx={{ p: 2, mb: 2 }}>
                New Rooms:{' '}
                {selectedNewRooms.map((n) => roomLabel(n)).join(', ')}
              </Paper>

              <Divider sx={{ my: 2 }} />

              <TextField
                label={`Extra Charge (${Companycurrency})`}
                type='number'
                fullWidth
                value={extraCharge}
                onChange={(e) => setExtraCharge(e.target.value)}
              />
              <TextField
                label='Remark'
                fullWidth
                multiline
                rows={3}
                sx={{ mt: 2 }}
                value={remark}
                onChange={(e) => setRemark(e.target.value)}
              />
            </>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>

          {step > 1 && <Button onClick={() => setStep(step - 1)}>Back</Button>}

          {step === 1 && (
            <Button disabled={!canGoNext} onClick={() => setStep(2)}>
              Next
            </Button>
          )}

          {step === 2 && <Button onClick={() => setStep(3)}>Next</Button>}

          {step === 3 && (
            <Button
              variant='contained'
              color='success'
              onClick={handleSubmit}
              disabled={
                submitting ||
                selectedNewRooms.length !== selectedOldRooms.length
              }
              startIcon={
                submitting ? (
                  <CircularProgress size={16} color='inherit' />
                ) : null
              }>
              {submitting ? 'Confirming...' : 'Confirm'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
